import type { AdRequestContext, AdType } from '@/modules/adv/types'
import type { AdProviderTypes } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

import { AdConsoleLogger } from '../utils/ad-console-logger'
import type { HbAdUnitDefinition, HeaderBiddingAuctionResult, PendingHbAuction } from './types'
import { hasValidVastMarkup } from './validate-video-bid'

const AUCTION_CACHE_TTL_CAP_MS = 300_000
const AUCTION_CACHE_TTL_BUFFER_MS = 30_000
const EMPTY_AUCTION_CACHE_TTL_MS = 60_000

export type AuctionCacheContext = {
    placement: AdType
    provider: AdProviderTypes
    waterfallIndex: number
    requestContext?: AdRequestContext
}

type AuctionCacheEventStatus = 'hit' | 'miss' | 'expired' | 'size_mismatch' | 'ad_unit_mismatch'

function getAuctionExpiresAt({ winningBid, timestamp }: HeaderBiddingAuctionResult) {
    return winningBid
        ? winningBid.responseTimestamp +
              Math.min(AUCTION_CACHE_TTL_CAP_MS, winningBid.ttl * 1000 - AUCTION_CACHE_TTL_BUFFER_MS)
        : (timestamp ?? Date.now()) + EMPTY_AUCTION_CACHE_TTL_MS
}

function getKey(context: AuctionCacheContext): string {
    return `${context.placement}:${context.waterfallIndex}:${context.provider}`
}

type CachedWinningBidSize = {
    isPlayableVideo: boolean
    width: number | undefined
    height: number | undefined
}

type CacheEntry = PendingHbAuction & { expiresAt: number; winningBidSize?: CachedWinningBidSize }

function fitsCurrentSizes(cachedSize: CachedWinningBidSize | undefined, freshAdUnit: HbAdUnitDefinition): boolean {
    if (!cachedSize || cachedSize.isPlayableVideo) {
        return true
    }

    const { width, height } = cachedSize
    if (!width || !height) {
        return true
    }

    return normalizeSizes(freshAdUnit.mediaTypes?.banner?.sizes).some(([w, h]) => w === width && h === height)
}

// banner.sizes is `Size | Size[]` (Size = [number, number]) - normalize to a flat list of pairs.
function normalizeSizes(sizes: [number, number] | [number, number][] | undefined): [number, number][] {
    if (!sizes || sizes.length === 0) {
        return []
    }
    return Array.isArray(sizes[0]) ? (sizes as [number, number][]) : [sizes as [number, number]]
}

export class AuctionCache {
    private readonly entries = new Map<string, CacheEntry>()

    private readonly adConsoleLogger = new AdConsoleLogger('hb', 'AuctionCache')

    constructor(
        private readonly runAuction: (
            adUnit: HbAdUnitDefinition,
            context: { placement: AdType; provider: AdProviderTypes; requestContext?: AdRequestContext },
        ) => Promise<HeaderBiddingAuctionResult | undefined>,
        private readonly logEvent: (event: LoggerEvent) => void,
    ) {}

    takeOrRun(
        getAdUnit: () => HbAdUnitDefinition | undefined,
        context: AuctionCacheContext,
    ): PendingHbAuction | undefined {
        const key = getKey(context)
        const cached = this.entries.get(key)
        this.entries.delete(key)

        const adUnit = getAdUnit()
        if (!adUnit) {
            return undefined
        }

        if (cached) {
            const isExpired = Date.now() >= cached.expiresAt
            const isSameAdUnit = cached.adUnit.code === adUnit.code
            if (!isExpired && isSameAdUnit && fitsCurrentSizes(cached.winningBidSize, adUnit)) {
                this.logCacheEvent('hit', context)
                return { adUnit: cached.adUnit, result: cached.result }
            }

            let missReason: AuctionCacheEventStatus = 'size_mismatch'
            if (isExpired) {
                missReason = 'expired'
            } else if (!isSameAdUnit) {
                missReason = 'ad_unit_mismatch'
            }
            this.logCacheEvent(missReason, context)
        } else {
            this.logCacheEvent('miss', context)
        }

        return {
            adUnit,
            result: this.runAuction(adUnit, {
                placement: context.placement,
                provider: context.provider,
                requestContext: context.requestContext,
            }),
        }
    }

    clear(): void {
        this.entries.clear()
    }

    put(pending: PendingHbAuction, context: AuctionCacheContext): void {
        pending.result.then(
            (auctionResult) => {
                // rejected/timed-out auctions and results already past their TTL are dropped
                if (!auctionResult) {
                    return
                }
                const expiresAt = getAuctionExpiresAt(auctionResult)
                if (expiresAt > Date.now()) {
                    const { winningBid } = auctionResult
                    const winningBidSize: CachedWinningBidSize | undefined = winningBid && {
                        isPlayableVideo: winningBid.mediaType === 'video' && hasValidVastMarkup(winningBid.vastXml),
                        width: winningBid.width,
                        height: winningBid.height,
                    }
                    this.entries.set(getKey(context), { ...pending, expiresAt, winningBidSize })
                }
            },
            () => {},
        )
    }

    private logCacheEvent(status: AuctionCacheEventStatus, context: AuctionCacheContext) {
        this.adConsoleLogger.debug(`auction cache ${status}`, context)
        this.logEvent({
            event: 'custom_event',
            eventName: 'ad_hb_auction_cache',
            adPlacement: context.placement,
            adProvider: context.provider,
            waterfallIndex: context.waterfallIndex + 1,
            dimension1: status,
        })
    }
}
